import React, { useMemo } from 'react';
import { 
  FiCheck, FiUser, FiClock, FiAlertCircle, FiMessageSquare, 
  FiPackage, FiTruck, FiMapPin, FiEdit
} from 'react-icons/fi';
import { format } from 'date-fns';
import { needsDispatchAttention } from '../utils/dispatchOrderBuckets';

const EVENT_ICONS = {
  dispatched: FiTruck,
  assigned: FiUser,
  created: FiPackage,
  updated: FiEdit,
  note: FiMessageSquare,
  alert: FiAlertCircle,
  time: FiClock,
  location: FiMapPin,
  completed: FiCheck,
};

const EVENT_COLORS = {
  dispatched: 'text-emerald-600 dark:text-emerald-400',
  assigned: 'text-sky-600 dark:text-sky-400',
  created: 'text-violet-600 dark:text-violet-400',
  updated: 'text-amber-600 dark:text-amber-400',
  note: 'text-gray-600 dark:text-gray-400',
  alert: 'text-coral-600 dark:text-coral-400',
  time: 'text-blue-600 dark:text-blue-400',
  location: 'text-purple-600 dark:text-purple-400',
  completed: 'text-green-600 dark:text-green-400',
};

export default function ActivityTimeline({ orders, filter = 'all' }) {
  const events = useMemo(() => {
    const allEvents = [];
    
    orders.forEach(order => {
      // Order created
      if (order.createdAt) {
        allEvents.push({
          type: 'created',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.createdAt),
          message: `Order #${order.uniqueId} created`,
        });
      }

      // Driver assigned
      if (order.driverID && order.updatedAt) {
        allEvents.push({
          type: 'assigned',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.updatedAt),
          message: `Concierge assigned to #${order.uniqueId}`,
        });
      }

      // Dispatched
      if (order.storeOrderId && order.storeArrivalTime) {
        allEvents.push({
          type: 'dispatched',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.storeArrivalTime),
          message: `Order #${order.uniqueId} dispatched`,
        });
      }

      // Notes added
      if (order.dispatchNotes && order.dispatchNotes.trim()) {
        allEvents.push({
          type: 'note',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.updatedAt || order.createdAt),
          message: `Note added to #${order.uniqueId}`,
        });
      }

      // Alerts
      if (needsDispatchAttention(order)) {
        allEvents.push({
          type: 'alert',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.updatedAt || order.createdAt),
          message: `Alert: #${order.uniqueId} needs attention`,
        });
      }

      // Completed
      if (order.status === 'completed') {
        allEvents.push({
          type: 'completed',
          orderId: order.uniqueId,
          order: order,
          timestamp: new Date(order.updatedAt || order.createdAt),
          message: `Order #${order.uniqueId} completed`,
        });
      }
    });

    // Sort by timestamp (newest first)
    return allEvents.sort((a, b) => b.timestamp - a.timestamp);
  }, [orders]);

  const filteredEvents = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(e => e.type === filter);
  }, [events, filter]);

  // Group by date
  const groupedEvents = useMemo(() => {
    const groups = {};
    filteredEvents.forEach(event => {
      const dateKey = format(event.timestamp, 'yyyy-MM-dd');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(event);
    });
    return groups;
  }, [filteredEvents]);

  return (
    <div className="space-y-6">
      {Object.entries(groupedEvents)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([dateKey, dateEvents]) => (
          <div key={dateKey}>
            <div className="sticky top-0 bg-gray-50 dark:bg-gray-800/50 backdrop-blur-sm z-10 py-2 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {format(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
              </h3>
            </div>
            
            <div className="relative pl-8 border-l-2 border-gray-200 dark:border-gray-700 space-y-4">
              {dateEvents.map((event, idx) => {
                const Icon = EVENT_ICONS[event.type] || FiPackage;
                const colorClass = EVENT_COLORS[event.type] || 'text-gray-600 dark:text-gray-400';
                
                return (
                  <div key={idx} className="relative">
                    {/* Timeline dot */}
                    <div className={`absolute -left-[33px] top-1 w-6 h-6 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center`}>
                      <Icon className={`w-3 h-3 ${colorClass}`} />
                    </div>
                    
                    {/* Event content */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {event.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {format(event.timestamp, 'h:mm a')}
                          </p>
                          {event.order && (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                                #{event.orderId}
                              </span>
                              {event.order.property?.address?.city && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  • {event.order.property.address.city}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      
      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <FiPackage className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No activity found</p>
        </div>
      )}
    </div>
  );
}
